import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, ArrowRight, CheckCircle2, RotateCcw, Palette, Loader2,
  ShieldCheck, TrendingUp, Wand2, Rocket, Wallet, ChevronRight, ChevronLeft,
  Plus, Save, Upload, Star, MessageSquare, Ban, Type, MapPin, Link2, Award,
  AlertCircle, Package, Globe, Trash2, Lock, Unlock, Copy, PlayCircle,
  ExternalLink, ImageIcon, X,
  Inbox, FileImage, Layout, FileUp, Camera, Clock, XCircle,
} from "lucide-react";
import {
  studioMe, studioOnbQuestions, studioOnbAnswer, studioOnbReset,
  studioOnbGenerateReadiness, studioOnbReadiness,
  studioBrandDnaCurrent, studioBrandDnaCreate, studioBrandDnaUpdate,
  studioBrandDnaPublish, studioBrandDnaVersions,
  studioCreditsTopup, studioUploadLogo,
  studioProductsList, studioProductImportUrl, studioProductDelete,
  studioCopyFrameworks, studioCopyGenerate, studioCopyVersions,
  studioCopyLock, studioCopyUnlock,
  studioAdGenerate, studioAdAssets,
  studioLandingGenerate, studioLandingList,
  studioApprovalsList, studioApprovalsSummary, studioApprovalsDecide,
  studioBulkImport, studioBulkImportCsv, studioBulkJob,
} from "../lib/api";

/* ==========================================================================
   Studio SPA shell — role-aware tabbed workspace at /studio.
   Tabs shipped in this iteration: Onboarding · Brand DNA.
   Later sprints will plug in Campaigns, Approvals, Analytics as new tabs.
   ========================================================================== */
export default function StudioApp() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("setup");

  useEffect(() => {
    document.title = "AI Ad & Growth Studio · WebsiteTech";
    const token = localStorage.getItem("wt_token");
    if (!token) {
      nav("/login?next=/studio", { replace: true });
      return;
    }
    (async () => {
      try {
        const m = await studioMe();
        setMe(m);
        if (m.onboarded) setTab("brand");
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) {
          nav("/login?next=/studio", { replace: true });
          return;
        }
        setErr(
          status === 403
            ? "The Ad & Growth Studio isn't turned on for your tenant yet. Please contact WebsiteTech to enable studio_v1."
            : e?.response?.data?.detail || e?.message || "Studio failed to load."
        );
      } finally { setLoading(false); }
    })();
  }, [nav]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fdfbf5] grid place-items-center">
        <div className="flex items-center gap-2 text-slate-600">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading Studio…
        </div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen bg-[#fdfbf5] grid place-items-center p-6">
        <div className="max-w-md text-center space-y-3">
          <ShieldCheck className="w-10 h-10 text-slate-400 mx-auto" />
          <div className="text-lg font-bold text-slate-900">Studio unavailable</div>
          <div className="text-sm text-slate-600">{err}</div>
          <button
            data-testid="studio-back-btn"
            onClick={() => nav("/portal")}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-semibold"
          >
            Back to portal
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: "setup", label: "Growth setup", Icon: Rocket },
    { key: "brand", label: "Brand DNA", Icon: Palette },
    { key: "campaigns", label: "Products & campaigns", Icon: Package },
    { key: "approvals", label: "Approvals", Icon: Inbox },
  ];

  return (
    <div className="min-h-screen bg-[#fdfbf5] text-slate-900" data-testid="studio-app">
      <div className="sticky top-0 z-40 bg-[#fdfbf5]/95 backdrop-blur-lg border-b border-[#ebe4d3]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 via-fuchsia-500 to-orange-500 grid place-items-center shadow-md shadow-indigo-500/20">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-base font-bold text-slate-900">AI Ad &amp; Growth Studio</div>
              <div className="text-xs uppercase tracking-widest text-slate-600">
                {me?.tenant_id} · Brand DNA {me?.brand_dna_version ? `v${me.brand_dna_version}` : "not published"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <CreditsPill me={me} onChanged={(m) => setMe(m)} />
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 flex gap-0 overflow-x-auto">
          {tabs.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`studio-tab-${key}`}
              className={`inline-flex items-center gap-2 px-5 py-3.5 text-sm font-semibold whitespace-nowrap transition relative ${
                tab === key ? "text-indigo-700" : "text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
              {tab === key && (
                <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-indigo-600 rounded-full" />
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {tab === "setup" && <SetupTab onDone={() => setTab("brand")} onBootstrap={() => studioMe().then(setMe)} />}
        {tab === "brand" && <BrandDnaTab onSaved={() => studioMe().then(setMe)} />}
        {tab === "campaigns" && <CampaignsTab onCredits={() => studioMe().then(setMe)} />}
        {tab === "approvals" && <ApprovalsTab />}
      </div>
    </div>
  );
}

/* ==========================================================================
   Setup tab — Onboarding wizard → Growth Readiness Report
   ========================================================================== */
function SetupTab({ onDone, onBootstrap }) {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [i, setI] = useState(0);
  const [busy, setBusy] = useState(false);
  const [savingId, setSavingId] = useState("");
  const [report, setReport] = useState(null);
  const [loadingState, setLoadingState] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const q = await studioOnbQuestions();
        setQuestions(q.questions || []);
        setAnswers(q.answers || {});
        // Jump to first unanswered
        const firstMissing = (q.questions || []).findIndex((qq) => (q.answers || {})[qq.id] === undefined);
        setI(firstMissing >= 0 ? firstMissing : (q.questions || []).length);
        // Load previously-generated report, if any
        try { const rep = await studioOnbReadiness(); setReport(rep.report); } catch { /* none yet */ }
      } catch (e) { setError(e?.response?.data?.detail || e?.message || "Failed to load questions"); }
      finally { setLoadingState(false); }
    })();
  }, []);

  const current = questions[i];
  const isDone = i >= questions.length;
  const totalAnswered = Object.keys(answers).length;

  const saveAnswer = async (qid, value) => {
    setSavingId(qid);
    try {
      const res = await studioOnbAnswer(qid, value);
      setAnswers(res.answers || {});
      setQuestions(res.questions || questions);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not save");
    } finally { setSavingId(""); }
  };

  const next = () => setI((v) => Math.min(v + 1, questions.length));
  const back = () => setI((v) => Math.max(0, v - 1));

  const generate = async () => {
    setBusy(true); setError("");
    try {
      const res = await studioOnbGenerateReadiness();
      setReport(res.report);
      onBootstrap && onBootstrap();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Generation failed");
    } finally { setBusy(false); }
  };

  const restart = async () => {
    setBusy(true);
    try {
      await studioOnbReset();
      setAnswers({}); setReport(null); setI(0);
      const q = await studioOnbQuestions();
      setQuestions(q.questions || []);
    } finally { setBusy(false); }
  };

  if (loadingState) {
    return <div className="text-sm text-slate-600 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading your setup…</div>;
  }

  if (report) {
    return (
      <div className="space-y-6" data-testid="readiness-report">
        <ReportCard report={report} />
        <div className="flex items-center gap-2">
          <button onClick={onDone} data-testid="readiness-continue-brand"
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white px-4 py-2 text-sm font-bold">
            Continue to Brand DNA <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={restart} data-testid="readiness-restart"
            className="inline-flex items-center gap-2 rounded-lg border border-[#dcd3bc] hover:border-[#c8bd9c] bg-white hover:bg-[#f5efdd] px-4 py-2 text-sm font-semibold">
            <RotateCcw className="w-3.5 h-3.5" /> Redo onboarding
          </button>
        </div>
      </div>
    );
  }

  const pct = questions.length ? Math.min(100, Math.round((totalAnswered / questions.length) * 100)) : 0;

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="onboarding-wizard">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-indigo-700 font-bold">Step 1 · Get to know your business</div>
        <div className="text-2xl font-bold mt-1 text-slate-900">Growth setup</div>
        <div className="text-sm text-slate-600 mt-1">
          Answer a few short questions so we can produce a personalized Growth Readiness Report and pre-fill your Brand DNA.
        </div>
      </div>

      {/* Progress */}
      <div className="rounded-full h-1.5 bg-[#f0e9d6] overflow-hidden">
        <div className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 transition-all duration-500"
          style={{ width: `${pct}%` }} />
      </div>
      <div className="text-xs text-slate-600 font-semibold">
        {totalAnswered} of {questions.length} answered
      </div>

      {error && (
        <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {!isDone && current && (
        <QuestionCard
          key={current.id}
          q={current}
          value={answers[current.id]}
          saving={savingId === current.id}
          onSave={(v) => saveAnswer(current.id, v)}
        />
      )}

      <div className="flex items-center justify-between pt-4 border-t border-[#ebe4d3]">
        <button onClick={back} disabled={i === 0} data-testid="wizard-back"
          className="inline-flex items-center gap-1 rounded-lg border border-[#dcd3bc] bg-white hover:bg-[#f5efdd] px-3 py-2 text-xs font-semibold disabled:opacity-40">
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </button>
        {!isDone ? (
          <button onClick={next} disabled={savingId !== "" || (current && answers[current.id] === undefined)}
            data-testid="wizard-next"
            className="inline-flex items-center gap-1 rounded-lg bg-indigo-500 hover:bg-indigo-400 disabled:opacity-40 text-white px-4 py-2 text-xs font-bold">
            Next <ChevronRight className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button onClick={generate} disabled={busy || totalAnswered < 6}
            data-testid="wizard-generate"
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 disabled:opacity-40 text-white px-5 py-2 text-sm font-bold">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating your report…</> : <><Wand2 className="w-4 h-4" /> Generate Growth Readiness Report</>}
          </button>
        )}
      </div>
    </div>
  );
}

function QuestionCard({ q, value, saving, onSave }) {
  const [draft, setDraft] = useState(value ?? (q.kind === "multi" ? [] : ""));
  useEffect(() => { setDraft(value ?? (q.kind === "multi" ? [] : "")); }, [q.id]); // eslint-disable-line

  const commit = (v) => { setDraft(v); onSave(v); };
  const toggleMulti = (opt) => {
    const arr = Array.isArray(draft) ? [...draft] : [];
    const j = arr.indexOf(opt);
    if (j >= 0) arr.splice(j, 1); else arr.push(opt);
    commit(arr);
  };

  return (
    <div className="rounded-2xl border border-[#ebe4d3] bg-white p-6 shadow-sm space-y-4" data-testid={`wizard-q-${q.id}`}>
      <div className="flex items-start gap-2">
        <div className="text-lg font-bold text-slate-900 flex-1">{q.question}</div>
        {saving && <Loader2 className="w-4 h-4 animate-spin text-slate-400 mt-1" />}
      </div>
      {q.kind === "text" && (
        <input type="text"
          data-testid={`wizard-q-input-${q.id}`}
          value={draft || ""}
          placeholder={q.placeholder || ""}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => draft !== value && commit(draft)}
          onKeyDown={(e) => { if (e.key === "Enter") commit(draft); }}
          className="w-full rounded-lg border border-[#dcd3bc] bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
      )}
      {q.kind === "single" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {q.options.map((opt) => (
            <button key={opt} onClick={() => commit(opt)}
              data-testid={`wizard-q-${q.id}-opt-${opt.slice(0,10)}`}
              className={`text-left rounded-lg border px-3 py-2 text-sm transition ${
                draft === opt
                  ? "border-indigo-500 bg-indigo-50 text-indigo-900 font-semibold"
                  : "border-[#dcd3bc] bg-white hover:bg-[#f5efdd] text-slate-800"
              }`}>
              {draft === opt && <CheckCircle2 className="inline w-3.5 h-3.5 mr-1.5 text-indigo-600" />}
              {opt}
            </button>
          ))}
        </div>
      )}
      {q.kind === "multi" && (
        <div className="flex flex-wrap gap-2">
          {q.options.map((opt) => {
            const active = Array.isArray(draft) && draft.includes(opt);
            return (
              <button key={opt} onClick={() => toggleMulti(opt)}
                data-testid={`wizard-q-${q.id}-opt-${opt.slice(0,10)}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                  active
                    ? "border-indigo-500 bg-indigo-500 text-white"
                    : "border-[#dcd3bc] bg-white hover:bg-[#f5efdd] text-slate-700"
                }`}>
                {active && <CheckCircle2 className="w-3 h-3" />}
                {opt}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ReportCard({ report }) {
  if (!report) return null;
  return (
    <div className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm p-6 space-y-5">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-indigo-700 font-bold">Growth Readiness Report</div>
        <div className="mt-1 text-sm text-slate-800 leading-relaxed">{report.business_summary}</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniKpi label="Tracking readiness" value={`${report.tracking_readiness_score ?? "—"} / 100`} accent="indigo" />
        <MiniKpi label="Recommended package" value={report.recommended_service_package || "—"} accent="fuchsia" />
        <MiniKpi label="Recommended channels" value={(report.recommended_channels || []).slice(0, 3).join(", ") || "—"} />
        <MiniKpi label="First campaign" value={report.suggested_first_campaign ? "Ready" : "—"} accent="emerald" />
      </div>

      <ReportSection title="Primary customer groups" items={report.primary_customer_groups} icon={Star} />
      <ReportSection title="Recommended WebsiteTech modules" items={report.recommended_wt_modules} icon={Rocket} />
      <ReportSection title="Suggested first campaign" items={[report.suggested_first_campaign]} icon={Sparkles} />
      <ReportSection title="Implementation steps" items={report.implementation_steps} icon={TrendingUp} numbered />
      {(report.missing_information || []).length > 0 && (
        <ReportSection title="Missing information" items={report.missing_information} icon={AlertCircle} />
      )}
    </div>
  );
}

function MiniKpi({ label, value, accent = "slate" }) {
  const map = {
    slate: "text-slate-900",
    indigo: "text-indigo-700",
    fuchsia: "text-fuchsia-700",
    emerald: "text-emerald-700",
  };
  return (
    <div className="rounded-xl border border-[#f0e9d6] bg-[#faf5e8] p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`mt-1 text-base font-bold ${map[accent]}`}>{value}</div>
    </div>
  );
}

function ReportSection({ title, items, icon: Icon = Star, numbered = false }) {
  if (!items || !items.length) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-600 font-bold flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      <ul className={`mt-2 space-y-1 text-sm text-slate-800 ${numbered ? "list-decimal ml-5" : "list-disc ml-5"}`}>
        {items.filter(Boolean).map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  );
}

/* ==========================================================================
   Brand DNA editor
   ========================================================================== */
function BrandDnaTab({ onSaved }) {
  const [dna, setDna] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [published, setPublished] = useState(false);
  const [error, setError] = useState("");
  const [section, setSection] = useState("business");

  const load = async () => {
    setLoading(true);
    try {
      const [d, v] = await Promise.all([studioBrandDnaCurrent(), studioBrandDnaVersions()]);
      setDna(d); setVersions(v.items || []);
      setPublished(!!d?.is_current);
    } catch (e) { setError(e?.response?.data?.detail || e?.message || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (path, value) => {
    setDna((prev) => {
      const clone = JSON.parse(JSON.stringify(prev));
      const parts = path.split(".");
      let ref = clone;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!ref[parts[i]] || typeof ref[parts[i]] !== "object") ref[parts[i]] = {};
        ref = ref[parts[i]];
      }
      ref[parts[parts.length - 1]] = value;
      return clone;
    });
  };

  const saveDraft = async () => {
    setSaving(true); setError("");
    try {
      // If we're editing the "unsaved seed", create a fresh v1 (or vN+1 if history exists)
      if (dna.version === null || dna.version === undefined || dna.unsaved) {
        const created = await studioBrandDnaCreate(sanitize(dna));
        setDna({ ...created, unsaved: false });
      } else if (dna.is_current) {
        // Published versions are immutable — clone into a new draft
        const created = await studioBrandDnaCreate(sanitize(dna));
        setDna({ ...created, unsaved: false });
      } else {
        await studioBrandDnaUpdate(dna.version, sanitize(dna));
      }
      const v = await studioBrandDnaVersions();
      setVersions(v.items || []);
    } catch (e) { setError(e?.response?.data?.detail || e?.message || "Save failed"); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    if (!dna || dna.version === null || dna.version === undefined) return;
    setSaving(true); setError("");
    try {
      // Save any pending edits first
      await studioBrandDnaUpdate(dna.version, sanitize(dna));
      await studioBrandDnaPublish(dna.version);
      setPublished(true);
      onSaved && onSaved();
      await load();
    } catch (e) { setError(e?.response?.data?.detail || e?.message || "Publish failed"); }
    finally { setSaving(false); }
  };

  if (loading || !dna) {
    return <div className="text-sm text-slate-600 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading Brand DNA…</div>;
  }

  const sections = [
    { key: "business", label: "Business", Icon: Rocket },
    { key: "identity", label: "Colours & Type", Icon: Palette },
    { key: "voice", label: "Voice & Tone", Icon: MessageSquare },
    { key: "destinations", label: "Contact + Links", Icon: Link2 },
    { key: "audiences", label: "Audiences & USPs", Icon: Star },
    { key: "testimonials", label: "Testimonials", Icon: Award },
    { key: "compliance", label: "Claims & Legal", Icon: Ban },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6" data-testid="brand-dna-editor">
      <aside className="space-y-1">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Brand DNA sections</div>
        {sections.map(({ key, label, Icon }) => (
          <button key={key} onClick={() => setSection(key)}
            data-testid={`brand-section-${key}`}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm inline-flex items-center gap-2 transition ${
              section === key
                ? "bg-indigo-500 text-white font-semibold shadow-sm shadow-indigo-500/30"
                : "text-slate-700 hover:bg-white"
            }`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
        <div className="mt-4 pt-4 border-t border-[#ebe4d3] text-[10px] uppercase tracking-widest text-slate-500 font-bold">Version</div>
        <div className="mt-2 rounded-lg border border-[#ebe4d3] bg-white px-3 py-2 text-xs">
          <div className="font-bold text-slate-900">
            {dna.version ? `v${dna.version}` : "Draft (unsaved)"}
          </div>
          <div className="text-slate-500">
            {dna.is_current ? "Currently published" : "Draft"}
          </div>
        </div>
        {versions.length > 0 && (
          <div className="mt-2 max-h-40 overflow-y-auto space-y-0.5 text-[11px]">
            {versions.map((v) => (
              <div key={v.version} className="flex items-center justify-between px-2 py-1 text-slate-600">
                <span>v{v.version}</span>
                {v.is_current && <span className="text-emerald-700 font-semibold">Current</span>}
              </div>
            ))}
          </div>
        )}
      </aside>

      <div className="space-y-5">
        {error && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        {section === "business" && (
          <Card title="Business identity">
            <FieldText label="Business name" value={dna.business?.name || ""} onChange={(v) => set("business.name", v)} tid="dna-business-name" />
            <FieldText label="Short description" value={dna.business?.description || ""} onChange={(v) => set("business.description", v)} tid="dna-business-desc" multiline />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FieldText label="Industry" value={dna.business?.industry || ""} onChange={(v) => set("business.industry", v)} tid="dna-business-industry" />
              <FieldText label="Sub-industry" value={dna.business?.sub_industry || ""} onChange={(v) => set("business.sub_industry", v)} tid="dna-business-sub" />
            </div>
            <FieldChips label="Locations served" values={dna.business?.locations || []} onChange={(v) => set("business.locations", v)} tid="dna-locations" placeholder="Add a city or region + Enter" />
          </Card>
        )}

        {section === "identity" && (
          <>
            <Card title="Brand colours">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {["primary", "secondary", "accent"].map((key) => (
                  <div key={key}>
                    <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{key}</label>
                    <div className="mt-1 flex items-center gap-2">
                      <input type="color"
                        data-testid={`dna-color-${key}`}
                        value={dna.identity?.colors?.[key] || "#000000"}
                        onChange={(e) => set(`identity.colors.${key}`, e.target.value)}
                        className="w-12 h-10 rounded border border-[#dcd3bc] bg-white cursor-pointer" />
                      <input type="text"
                        value={dna.identity?.colors?.[key] || ""}
                        onChange={(e) => set(`identity.colors.${key}`, e.target.value)}
                        className="flex-1 rounded-lg border border-[#dcd3bc] bg-white px-3 py-2 text-sm font-mono outline-none focus:border-indigo-500" />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
            <Card title="Typography">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldText label="Heading font" value={dna.identity?.typography?.heading || ""} onChange={(v) => set("identity.typography.heading", v)} tid="dna-font-heading" />
                <FieldText label="Body font" value={dna.identity?.typography?.body || ""} onChange={(v) => set("identity.typography.body", v)} tid="dna-font-body" />
              </div>
            </Card>
            <Card title="Logos">
              <LogoUploader
                values={dna.identity?.logos || []}
                onChange={(v) => set("identity.logos", v)}
              />
            </Card>
          </>
        )}

        {section === "voice" && (
          <Card title="Voice & Tone">
            <FieldSelect label="Preferred tone" value={dna.voice?.tone || ""} onChange={(v) => set("voice.tone", v)}
              options={["Professional", "Friendly", "Reassuring", "Premium", "Educational", "Community-focused", "Urgent but honest", "Conservative", "Energetic"]}
              tid="dna-voice-tone" />
            <FieldText label="Writing style" value={dna.voice?.writing_style || ""} onChange={(v) => set("voice.writing_style", v)} tid="dna-voice-style" placeholder="Clear + concise · No jargon" />
            <FieldChips label="Languages" values={dna.voice?.languages || []} onChange={(v) => set("voice.languages", v)} tid="dna-languages" placeholder="Add a language + Enter" />
            <FieldGlossary label="Translation glossary — terms to preserve" pairs={dna.voice?.translation_glossary || {}} onChange={(v) => set("voice.translation_glossary", v)} tid="dna-glossary" />
          </Card>
        )}

        {section === "destinations" && (
          <Card title="Where should customers land?">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FieldText label="Website URL" value={dna.destinations?.website || ""} onChange={(v) => set("destinations.website", v)} tid="dna-dest-website" />
              <FieldText label="Phone" value={dna.destinations?.phone || ""} onChange={(v) => set("destinations.phone", v)} tid="dna-dest-phone" />
              <FieldText label="WhatsApp number" value={dna.destinations?.whatsapp || ""} onChange={(v) => set("destinations.whatsapp", v)} tid="dna-dest-whatsapp" />
              <FieldText label="Booking link" value={dna.destinations?.booking || ""} onChange={(v) => set("destinations.booking", v)} tid="dna-dest-booking" />
              <FieldText label="Email" value={dna.destinations?.email || ""} onChange={(v) => set("destinations.email", v)} tid="dna-dest-email" />
            </div>
            <div className="pt-3 mt-2 border-t border-[#f0e9d6]">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2">Social handles</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {["instagram", "facebook", "linkedin", "youtube"].map((k) => (
                  <FieldText key={k} label={k} value={dna.destinations?.social?.[k] || ""} onChange={(v) => set(`destinations.social.${k}`, v)} tid={`dna-social-${k}`} />
                ))}
              </div>
            </div>
          </Card>
        )}

        {section === "audiences" && (
          <>
            <Card title="Target audiences">
              <FieldList label="Audience labels" values={dna.audiences || []} onChange={(v) => set("audiences", v)} tid="dna-audiences" placeholder='e.g. "Working parents in Kochi, ages 30-45"' />
            </Card>
            <Card title="Unique selling propositions">
              <FieldList label="USPs" values={dna.usps || []} onChange={(v) => set("usps", v)} tid="dna-usps" placeholder="What makes you different?" />
            </Card>
            <Card title="Calls to action">
              <FieldList label="CTAs" values={dna.ctas || []} onChange={(v) => set("ctas", v)} tid="dna-ctas" placeholder='e.g. "Book a free consult"' />
            </Card>
          </>
        )}

        {section === "testimonials" && (
          <Card title="Approved testimonials">
            <FieldTestimonials values={dna.testimonials || []} onChange={(v) => set("testimonials", v)} />
          </Card>
        )}

        {section === "compliance" && (
          <>
            <Card title="Permitted vs prohibited claims">
              <FieldList label="Permitted claims" values={dna.permitted_claims || []} onChange={(v) => set("permitted_claims", v)} tid="dna-permitted" placeholder="Claims you're happy the AI can use" />
              <FieldList label="Prohibited claims" values={dna.prohibited_claims || []} onChange={(v) => set("prohibited_claims", v)} tid="dna-prohibited" placeholder="Claims the AI must never make" />
              <FieldChips label="Restricted words" values={dna.restricted_words || []} onChange={(v) => set("restricted_words", v)} tid="dna-restricted" placeholder="Word + Enter" />
            </Card>
            <Card title="Legal disclaimers">
              <FieldList label="Disclaimers" values={dna.disclaimers || []} onChange={(v) => set("disclaimers", v)} tid="dna-disclaimers" placeholder="Any regulatory or legal disclaimer" />
            </Card>
          </>
        )}

        <div className="sticky bottom-4 flex items-center gap-2 rounded-2xl bg-[#faf5e8] border border-[#dcd3bc] p-3 shadow-sm">
          <button onClick={saveDraft} disabled={saving}
            data-testid="brand-save-draft"
            className="inline-flex items-center gap-2 rounded-lg border border-[#dcd3bc] hover:border-[#c8bd9c] bg-white hover:bg-[#f5efdd] px-4 py-2 text-sm font-semibold disabled:opacity-40">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save as draft
          </button>
          <button onClick={publish} disabled={saving || dna.version === null || dna.version === undefined}
            data-testid="brand-publish"
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white px-4 py-2 text-sm font-bold disabled:opacity-40">
            <CheckCircle2 className="w-3.5 h-3.5" /> {published && dna.is_current ? "Republish" : "Publish this version"}
          </button>
          {published && dna.is_current && (
            <span className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Published
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* Small reusable field primitives ------------------------------------------ */
function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-[#ebe4d3] bg-white p-5 shadow-sm space-y-3">
      <div className="text-sm font-bold text-slate-900">{title}</div>
      {children}
    </div>
  );
}

function FieldText({ label, value, onChange, tid, multiline, placeholder }) {
  const cn = "w-full rounded-lg border border-[#dcd3bc] bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100";
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</label>
      {multiline ? (
        <textarea data-testid={tid} value={value || ""} placeholder={placeholder || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3} className={cn + " mt-1"} />
      ) : (
        <input type="text" data-testid={tid} value={value || ""} placeholder={placeholder || ""}
          onChange={(e) => onChange(e.target.value)} className={cn + " mt-1"} />
      )}
    </div>
  );
}

function FieldSelect({ label, value, onChange, options, tid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</label>
      <select data-testid={tid} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-[#dcd3bc] bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500">
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function FieldChips({ label, values, onChange, placeholder, tid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if ((values || []).includes(v)) { setDraft(""); return; }
    onChange([...(values || []), v]); setDraft("");
  };
  const remove = (i) => {
    const arr = [...(values || [])]; arr.splice(i, 1); onChange(arr);
  };
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</label>
      <div className="mt-1 flex items-center gap-2">
        <input type="text" data-testid={tid} value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder || ""}
          className="flex-1 rounded-lg border border-[#dcd3bc] bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
        <button onClick={add} className="rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white px-3 py-2 text-xs font-bold"><Plus className="w-3.5 h-3.5" /></button>
      </div>
      {(values || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(values || []).map((v, i) => (
            <span key={i} className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-800 text-xs font-semibold rounded-full px-2.5 py-1 border border-indigo-100">
              {v}
              <button onClick={() => remove(i)} className="hover:text-indigo-500">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldList({ label, values, onChange, placeholder, tid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim(); if (!v) return;
    onChange([...(values || []), v]); setDraft("");
  };
  const remove = (i) => { const a = [...(values || [])]; a.splice(i, 1); onChange(a); };
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</label>
      <div className="mt-1 flex items-center gap-2">
        <input type="text" data-testid={tid} value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder || ""}
          className="flex-1 rounded-lg border border-[#dcd3bc] bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
        <button onClick={add} className="rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white px-3 py-2 text-xs font-bold"><Plus className="w-3.5 h-3.5" /></button>
      </div>
      {(values || []).length > 0 && (
        <ul className="mt-2 space-y-1 text-sm">
          {(values || []).map((v, i) => (
            <li key={i} className="flex items-start gap-2 bg-[#faf5e8] border border-[#f0e9d6] rounded-lg px-3 py-1.5">
              <span className="flex-1 text-slate-800">{v}</span>
              <button onClick={() => remove(i)} className="text-slate-400 hover:text-rose-500 text-xs">✕</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FieldGlossary({ label, pairs, onChange, tid }) {
  const [k, setK] = useState(""); const [v, setV] = useState("");
  const list = Object.entries(pairs || {});
  const add = () => {
    if (!k.trim() || !v.trim()) return;
    onChange({ ...(pairs || {}), [k.trim()]: v.trim() });
    setK(""); setV("");
  };
  const remove = (key) => {
    const p = { ...(pairs || {}) }; delete p[key]; onChange(p);
  };
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</label>
      <div className="mt-1 grid grid-cols-[1fr_1fr_auto] gap-2">
        <input type="text" data-testid={tid + "-key"} value={k} onChange={(e) => setK(e.target.value)}
          placeholder="Original term" className="rounded-lg border border-[#dcd3bc] bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500" />
        <input type="text" data-testid={tid + "-val"} value={v} onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Keep exactly as…" className="rounded-lg border border-[#dcd3bc] bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500" />
        <button onClick={add} className="rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white px-3 py-2 text-xs font-bold"><Plus className="w-3.5 h-3.5" /></button>
      </div>
      {list.length > 0 && (
        <div className="mt-2 space-y-1 text-sm">
          {list.map(([kk, vv]) => (
            <div key={kk} className="flex items-center gap-2 bg-[#faf5e8] border border-[#f0e9d6] rounded-lg px-3 py-1.5">
              <span className="font-mono text-xs text-slate-700">{kk}</span>
              <span className="text-slate-400 text-xs">→</span>
              <span className="flex-1 text-slate-800">{vv}</span>
              <button onClick={() => remove(kk)} className="text-slate-400 hover:text-rose-500 text-xs">✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldTestimonials({ values, onChange }) {
  const [author, setAuthor] = useState(""); const [text, setText] = useState("");
  const add = () => {
    if (!text.trim()) return;
    onChange([...(values || []), { author: author.trim() || "Customer", text: text.trim(), added_at: new Date().toISOString() }]);
    setAuthor(""); setText("");
  };
  const remove = (i) => { const a = [...(values || [])]; a.splice(i, 1); onChange(a); };
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2">
        <input type="text" data-testid="dna-testimonial-author" value={author}
          onChange={(e) => setAuthor(e.target.value)} placeholder="Author"
          className="rounded-lg border border-[#dcd3bc] bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500" />
        <textarea data-testid="dna-testimonial-text" value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Approved testimonial quote…" rows={2}
          className="rounded-lg border border-[#dcd3bc] bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500" />
      </div>
      <button onClick={add} data-testid="dna-testimonial-add"
        className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white px-3 py-2 text-xs font-bold">
        <Plus className="w-3.5 h-3.5" /> Add testimonial
      </button>
      <div className="text-[11px] text-slate-500">Only add testimonials you have explicit permission to use.</div>
      {(values || []).length > 0 && (
        <div className="mt-2 space-y-2">
          {(values || []).map((t, i) => (
            <div key={i} className="rounded-lg border border-[#f0e9d6] bg-[#faf5e8] p-3 flex items-start gap-2">
              <div className="flex-1">
                <div className="text-xs font-bold text-slate-700">{t.author}</div>
                <div className="text-sm text-slate-800 italic">"{t.text}"</div>
              </div>
              <button onClick={() => remove(i)} className="text-slate-400 hover:text-rose-500 text-xs">✕</button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* Strip server-managed fields before sending an update payload */
function sanitize(dna) {
  const copy = { ...dna };
  ["id", "tenant_id", "version", "is_current", "unsaved",
   "created_at", "updated_at", "retired_at",
   "created_by", "approved_by", "approved_at"].forEach((k) => delete copy[k]);
  return copy;
}

/* ==========================================================================
   Credits pill with one-tap top-up + insufficient-credits helper
   ========================================================================== */
function CreditsPill({ me, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [showTopup, setShowTopup] = useState(false);
  const [amount, setAmount] = useState(500);
  const balance = me?.credits_balance ?? 0;
  const lowBalance = balance < 5;

  const topup = async () => {
    setBusy(true);
    try {
      await studioCreditsTopup(amount);
      setShowTopup(false);
      const m = await studioMe();
      onChanged && onChanged(m);
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Top up failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowTopup((v) => !v)}
        data-testid="studio-credits"
        className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition ${
          lowBalance
            ? "border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100"
            : "border-[#dcd3bc] bg-white hover:bg-[#f5efdd]"
        }`}
        title="Credit balance — click to top up"
      >
        <Wallet className={`w-3.5 h-3.5 ${lowBalance ? "text-amber-600" : "text-emerald-600"}`} />
        {balance} credits
        {lowBalance && <span className="ml-1 text-[10px] font-bold">LOW</span>}
      </button>
      {showTopup && (
        <div className="absolute right-0 mt-2 w-72 rounded-xl border border-[#ebe4d3] bg-white shadow-xl p-4 z-50"
             data-testid="studio-topup-panel">
          <div className="text-sm font-bold text-slate-900">Top up credits</div>
          <div className="text-xs text-slate-600 mt-1">
            Credits pay for AI runs (readiness reports, page imports, copy).
          </div>
          <div className="mt-3 grid grid-cols-3 gap-1.5">
            {[100, 500, 2000].map((v) => (
              <button key={v} onClick={() => setAmount(v)}
                className={`rounded-lg border px-2 py-1.5 text-xs font-semibold ${
                  amount === v ? "border-indigo-500 bg-indigo-50 text-indigo-900" : "border-[#dcd3bc] hover:border-indigo-300"
                }`}>
                +{v}
              </button>
            ))}
          </div>
          <input type="number" data-testid="studio-topup-amount"
            value={amount} min={10} max={100000}
            onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
            className="mt-2 w-full rounded-lg border border-[#dcd3bc] bg-white px-3 py-2 text-sm font-mono outline-none focus:border-indigo-500" />
          <button onClick={topup} disabled={busy || amount <= 0}
            data-testid="studio-topup-submit"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white px-3 py-2 text-sm font-bold disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
            Add {amount} credits
          </button>
          <div className="mt-2 text-[10px] text-slate-500">
            Demo tenants: added instantly. Real billing lands in Sprint 10.
          </div>
        </div>
      )}
    </div>
  );
}

/* Helper — pretty-prints a 402 InsufficientCredits error into a toast-friendly string */
function creditsErrorText(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (d.error === "insufficient_credits") {
    return `${d.message} (need ${d.required}, have ${d.balance})`;
  }
  return d.message || d.detail || JSON.stringify(d);
}


/* ==========================================================================
   LogoUploader — file drop + preview grid, hits /api/studio/uploads/logo
   ========================================================================== */
function LogoUploader({ values, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setError("");
    try {
      const res = await studioUploadLogo(file);
      onChange([...(values || []), res.url]);
    } catch (e) {
      setError(creditsErrorText(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (i) => {
    const arr = [...(values || [])]; arr.splice(i, 1); onChange(arr);
  };

  const resolve = (u) => {
    if (!u) return "";
    if (u.startsWith("http")) return u;
    if (u.startsWith("/api/")) return BACKEND + u;
    return u;
  };

  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Logo files</label>
      <div className="mt-2 flex flex-wrap gap-3">
        {(values || []).map((u, i) => (
          <div key={i} className="relative w-24 h-24 rounded-lg border border-[#ebe4d3] bg-white grid place-items-center overflow-hidden">
            <img src={resolve(u)} alt="Logo" className="max-w-full max-h-full object-contain p-1" />
            <button onClick={() => remove(i)} data-testid={`dna-logo-remove-${i}`}
              className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-slate-900/70 text-white grid place-items-center hover:bg-rose-500">
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <label className={`w-24 h-24 rounded-lg border-2 border-dashed grid place-items-center cursor-pointer transition ${
            busy ? "border-indigo-300 bg-indigo-50" : "border-[#dcd3bc] hover:border-indigo-400 hover:bg-indigo-50"
          }`}>
          {busy ? <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" /> :
            <div className="flex flex-col items-center gap-1 text-slate-500 text-[10px] font-semibold">
              <Upload className="w-4 h-4" /> Upload
            </div>}
          <input ref={inputRef} type="file" className="hidden"
            data-testid="dna-logo-upload"
            accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
            onChange={(e) => upload(e.target.files?.[0])} />
        </label>
      </div>
      {error && (
        <div className="mt-2 text-xs text-rose-700 flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5" /> {error}
        </div>
      )}
      <div className="mt-2 text-[11px] text-slate-500">
        PNG, JPG, WebP, SVG or GIF · up to 3 MB
      </div>
    </div>
  );
}


/* ==========================================================================
   Campaigns tab — Products list + URL importer + AI Copywriter
   ========================================================================== */
function CampaignsTab({ onCredits }) {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importError, setImportError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await studioProductsList();
      setProducts(r.items || []);
      if (!selected && (r.items || []).length > 0) setSelected(r.items[0]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const doImport = async () => {
    if (!importUrl.trim()) return;
    setImporting(true); setImportError("");
    try {
      const r = await studioProductImportUrl(importUrl.trim());
      setImportUrl("");
      await load();
      setSelected(r.product);
      onCredits && onCredits();
    } catch (e) {
      setImportError(creditsErrorText(e));
    } finally { setImporting(false); }
  };

  const removeProduct = async (id) => {
    if (!confirm("Delete this product? Locked copy versions will remain.")) return;
    await studioProductDelete(id);
    if (selected?.id === id) setSelected(null);
    await load();
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-6" data-testid="campaigns-tab">
      <aside className="space-y-4">
        <div className="rounded-2xl border border-[#ebe4d3] bg-white p-4 shadow-sm">
          <div className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5 text-indigo-600" /> Import a page
          </div>
          <div className="text-xs text-slate-600 mb-2">
            Paste any product, service or menu URL — we'll extract the offer, benefits and imagery into a campaign brief.
          </div>
          <div className="flex gap-2">
            <input type="url" data-testid="campaigns-import-url"
              value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") doImport(); }}
              placeholder="https://…"
              className="flex-1 rounded-lg border border-[#dcd3bc] bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500" />
            <button onClick={doImport} disabled={importing || !importUrl.trim()}
              data-testid="campaigns-import-submit"
              className="rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white px-3 py-2 text-xs font-bold disabled:opacity-40 inline-flex items-center gap-1">
              {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
              Import
            </button>
          </div>
          {importError && (
            <div className="mt-2 text-xs text-rose-700 flex items-start gap-1">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {importError}
            </div>
          )}
          <div className="mt-2 text-[10px] text-slate-500">Costs 3 credits per import.</div>
        </div>

        <BulkImportPanel onDone={() => { load(); onCredits && onCredits(); }} />

        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
            Your products &amp; services ({products.length})
          </div>
          {loading ? (
            <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : products.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[#dcd3bc] bg-white/50 p-4 text-xs text-slate-500 text-center">
              Import a URL above to get started.
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[540px] overflow-y-auto pr-1">
              {products.map((p) => (
                <button key={p.id} onClick={() => setSelected(p)}
                  data-testid={`campaigns-product-${p.id}`}
                  className={`w-full text-left rounded-lg px-3 py-2.5 border transition ${
                    selected?.id === p.id
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-[#ebe4d3] bg-white hover:bg-[#f5efdd]"
                  }`}>
                  <div className="text-xs font-bold text-slate-900 line-clamp-1">{p.title || "Untitled"}</div>
                  <div className="text-[11px] text-slate-500 line-clamp-2">{p.description || p.source_url}</div>
                  {p.price && (
                    <div className="mt-1 text-[11px] font-mono text-slate-700">{p.currency || "INR"} {p.price}</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </aside>

      <section>
        {selected ? (
          <ProductDetail product={selected} onDelete={removeProduct} onCredits={onCredits} />
        ) : (
          <div className="rounded-2xl border border-dashed border-[#dcd3bc] bg-white/60 p-10 text-center text-slate-500">
            <Package className="w-10 h-10 mx-auto text-slate-300" />
            <div className="mt-3 text-sm">Import a URL or pick a product to see its brief and generate copy.</div>
          </div>
        )}
      </section>
    </div>
  );
}


function ProductDetail({ product, onDelete, onCredits }) {
  const [tab, setTab] = useState("brief");
  return (
    <div className="space-y-4" data-testid="product-detail">
      <div className="rounded-2xl border border-[#ebe4d3] bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest text-indigo-700 font-bold">
              {product.kind || "product"}
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-1">{product.title}</div>
            {product.source_url && (
              <a href={product.source_url} target="_blank" rel="noopener noreferrer"
                className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1 mt-1">
                {product.source_url.slice(0, 60)}{product.source_url.length > 60 ? "…" : ""}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          <div className="flex items-center gap-2">
            {product.price && (
              <div className="rounded-lg bg-[#faf5e8] border border-[#f0e9d6] px-3 py-1.5 text-sm font-mono">
                {product.currency || "INR"} {product.price}
              </div>
            )}
            <button onClick={() => onDelete(product.id)}
              data-testid="product-delete"
              className="rounded-lg border border-[#dcd3bc] hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 text-slate-500 p-2">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="mt-3 text-sm text-slate-800 leading-relaxed">{product.description}</div>
        <div className="mt-3 flex gap-0 border-b border-[#f0e9d6] overflow-x-auto">
          {[
            { key: "brief", label: "Campaign brief" },
            { key: "copy", label: "AI Copywriter" },
            { key: "ads", label: "Static Ads" },
            { key: "landing", label: "Landing page" },
          ].map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`product-tab-${t.key}`}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest relative transition whitespace-nowrap ${
                tab === t.key ? "text-indigo-700" : "text-slate-500 hover:text-slate-800"
              }`}>
              {t.label}
              {tab === t.key && (
                <span className="absolute bottom-0 left-1 right-1 h-0.5 bg-indigo-600 rounded-full" />
              )}
            </button>
          ))}
        </div>
      </div>

      {tab === "brief" && <BriefView product={product} />}
      {tab === "copy" && <CopywriterView product={product} onCredits={onCredits} />}
      {tab === "ads" && <StaticAdsView product={product} onCredits={onCredits} />}
      {tab === "landing" && <LandingView product={product} />}
    </div>
  );
}

function BriefView({ product }) {
  return (
    <div className="rounded-2xl border border-[#ebe4d3] bg-white p-5 shadow-sm space-y-4">
      <BriefSection label="Benefits" items={product.benefits || []} />
      <BriefSection label="Proof" items={product.proof || []} />
      <BriefField label="Audience" value={product.audience_hint} />
      <BriefField label="Primary CTA" value={product.cta_hint} />
      {(product.legal_restrictions || []).length > 0 && (
        <BriefSection label="Legal restrictions" items={product.legal_restrictions} tone="amber" />
      )}
      {product.notes && (
        <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2 text-xs text-indigo-900">
          <span className="font-bold">Notes for marketing:</span> {product.notes}
        </div>
      )}
      {(product.images || []).length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2">Candidate images</div>
          <div className="flex flex-wrap gap-2">
            {product.images.map((u, i) => (
              <div key={i} className="w-24 h-24 rounded-lg border border-[#ebe4d3] bg-white grid place-items-center overflow-hidden">
                <img src={u} alt="" className="max-w-full max-h-full object-contain" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
function BriefSection({ label, items, tone = "slate" }) {
  if (!items?.length) return null;
  const bg = tone === "amber" ? "bg-amber-50 text-amber-900 border-amber-200" : "bg-[#faf5e8] text-slate-800 border-[#f0e9d6]";
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <span key={i} className={`text-xs px-2.5 py-1 rounded-full border ${bg}`}>{it}</span>
        ))}
      </div>
    </div>
  );
}
function BriefField({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}


function CopywriterView({ product, onCredits }) {
  const [frameworks, setFrameworks] = useState({ frameworks: [], tones: [], lengths: [] });
  const [framework, setFramework] = useState("AIDA");
  const [tone, setTone] = useState("Friendly");
  const [length, setLength] = useState("medium");
  const [variants, setVariants] = useState(3);
  const [goal, setGoal] = useState("");
  const [cta, setCta] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    studioCopyFrameworks().then(setFrameworks);
    studioCopyVersions(product.id).then((r) => setItems(r.items || []));
  }, [product.id]);

  const generate = async () => {
    setBusy(true); setError("");
    try {
      const r = await studioCopyGenerate({
        product_id: product.id, framework, tone, length,
        variants: Math.max(1, Math.min(5, variants)),
        goal: goal || null, cta: cta || null, language: "English",
      });
      setItems((prev) => [...(r.variants || []), ...prev]);
      onCredits && onCredits();
    } catch (e) {
      setError(creditsErrorText(e));
    } finally { setBusy(false); }
  };

  const toggleLock = async (v) => {
    try {
      if (v.locked) await studioCopyUnlock(v.id); else await studioCopyLock(v.id);
      const r = await studioCopyVersions(product.id);
      setItems(r.items || []);
    } catch (e) { alert(creditsErrorText(e)); }
  };

  const copy = (v) => {
    const text = `${v.headline}\n\n${v.body}\n\n${v.cta}`;
    navigator.clipboard?.writeText(text);
    setCopied(v.id); setTimeout(() => setCopied(null), 1600);
  };

  const cost = { short: 4, medium: 6, long: 10 }[length];

  return (
    <div className="space-y-4" data-testid="copywriter-view">
      <div className="rounded-2xl border border-[#ebe4d3] bg-white p-5 shadow-sm space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Framework</div>
            <select data-testid="copywriter-framework" value={framework} onChange={(e) => setFramework(e.target.value)}
              className="mt-1 w-full rounded-lg border border-[#dcd3bc] bg-white px-2 py-1.5 text-sm outline-none focus:border-indigo-500">
              {(frameworks.frameworks || []).map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Tone</div>
            <select data-testid="copywriter-tone" value={tone} onChange={(e) => setTone(e.target.value)}
              className="mt-1 w-full rounded-lg border border-[#dcd3bc] bg-white px-2 py-1.5 text-sm outline-none focus:border-indigo-500">
              {(frameworks.tones || []).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Length</div>
            <select data-testid="copywriter-length" value={length} onChange={(e) => setLength(e.target.value)}
              className="mt-1 w-full rounded-lg border border-[#dcd3bc] bg-white px-2 py-1.5 text-sm outline-none focus:border-indigo-500">
              {(frameworks.lengths || []).map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Variants</div>
            <input type="number" min={1} max={5} value={variants}
              data-testid="copywriter-variants"
              onChange={(e) => setVariants(parseInt(e.target.value) || 3)}
              className="mt-1 w-full rounded-lg border border-[#dcd3bc] bg-white px-2 py-1.5 text-sm outline-none focus:border-indigo-500" />
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input type="text" data-testid="copywriter-goal" value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="Campaign goal (optional) — e.g. Fill Saturday brunch slots"
            className="rounded-lg border border-[#dcd3bc] bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500" />
          <input type="text" data-testid="copywriter-cta" value={cta}
            onChange={(e) => setCta(e.target.value)}
            placeholder="Preferred CTA (optional) — e.g. Book now"
            className="rounded-lg border border-[#dcd3bc] bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500" />
        </div>
        <div className="flex items-center justify-between gap-2 pt-2">
          <div className="text-xs text-slate-500">
            Estimated cost: <span className="font-bold text-slate-900">{cost} credits</span> · uses your published Brand DNA
          </div>
          <button onClick={generate} disabled={busy}
            data-testid="copywriter-generate"
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white px-4 py-2 text-sm font-bold disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            Generate copy
          </button>
        </div>
        {error && (
          <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm px-3 py-2 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
          </div>
        )}
      </div>

      {items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-[#dcd3bc] bg-white/60 p-8 text-center text-slate-500 text-sm">
          No copy yet — pick a framework and tone above, then hit Generate.
        </div>
      )}
      {items.length > 0 && (
        <div className="space-y-3">
          {items.map((v) => (
            <div key={v.id} className={`rounded-2xl border p-5 shadow-sm ${
                v.locked ? "border-emerald-300 bg-emerald-50/40" : "border-[#ebe4d3] bg-white"
              }`} data-testid={`copywriter-variant-${v.id}`}>
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest bg-indigo-500 text-white font-bold px-2 py-0.5 rounded-full">{v.framework}</span>
                  <span className="text-[10px] uppercase tracking-widest bg-slate-800 text-white font-bold px-2 py-0.5 rounded-full">{v.tone}</span>
                  <span className="text-[10px] uppercase tracking-widest bg-slate-200 text-slate-800 font-bold px-2 py-0.5 rounded-full">{v.length}</span>
                  {(v.safety_flags || []).length > 0 && (
                    <span className="text-[10px] uppercase tracking-widest bg-rose-500 text-white font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                      <AlertCircle className="w-2.5 h-2.5" /> flagged: {v.safety_flags.join(", ")}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => copy(v)} data-testid={`copywriter-copy-${v.id}`}
                    className="inline-flex items-center gap-1 rounded-lg border border-[#dcd3bc] hover:border-indigo-400 bg-white px-2.5 py-1.5 text-[11px] font-semibold">
                    {copied === v.id ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                    {copied === v.id ? "Copied" : "Copy"}
                  </button>
                  <button onClick={() => toggleLock(v)} data-testid={`copywriter-lock-${v.id}`}
                    className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold border ${
                      v.locked ? "bg-emerald-500 text-white border-emerald-500" : "bg-white border-[#dcd3bc] hover:border-emerald-400"
                    }`}>
                    {v.locked ? <><Lock className="w-3 h-3" /> Locked</> : <><Unlock className="w-3 h-3" /> Lock</>}
                  </button>
                </div>
              </div>
              <div className="text-lg font-bold text-slate-900">{v.headline}</div>
              <div className="mt-2 text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{v.body}</div>
              <div className="mt-2 inline-block rounded-lg bg-indigo-500 text-white px-3 py-1.5 text-xs font-bold">{v.cta}</div>
              {v.why_it_works && (
                <div className="mt-3 text-[11px] text-slate-500 italic">Why this works: {v.why_it_works}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ==========================================================================
   Static Ad Studio view — one card per size, powered by Gemini Nano Banana
   ========================================================================== */
const AD_SIZE_DEFS = [
  { key: "1:1",  label: "Square (feed)",       aspect: "1 / 1"   },
  { key: "4:5",  label: "Portrait (feed)",     aspect: "4 / 5"   },
  { key: "9:16", label: "Story / Reel",        aspect: "9 / 16"  },
  { key: "16:9", label: "Landscape / display", aspect: "16 / 9"  },
];

function StaticAdsView({ product, onCredits }) {
  const [versions, setVersions] = useState([]);
  const [selectedVer, setSelectedVer] = useState(null);
  const [sizes, setSizes] = useState(["1:1", "4:5", "9:16", "16:9"]);
  const [assets, setAssets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

  const loadVersions = () =>
    studioCopyVersions(product.id).then((r) => {
      const locked = (r.items || []).filter((v) => v.locked);
      setVersions(locked);
      if (locked.length && !selectedVer) setSelectedVer(locked[0].id);
    });
  const loadAssets = () =>
    studioAdAssets({ product_id: product.id }).then((r) => setAssets(r.items || []));

  useEffect(() => { loadVersions(); loadAssets(); }, [product.id]); // eslint-disable-line

  const toggleSize = (s) => {
    setSizes((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  };

  const generate = async () => {
    if (!selectedVer) return;
    setBusy(true); setError("");
    try {
      const r = await studioAdGenerate({ version_id: selectedVer, sizes,
                                          product_id: product.id });
      onCredits && onCredits();
      await loadAssets();
      if (r.succeeded === 0) {
        setError("Ad generation failed for every size — placeholder assets stored. Regenerate to try again.");
      }
    } catch (e) {
      setError(creditsErrorText(e));
    } finally { setBusy(false); }
  };

  const resolveUrl = (u) => u?.startsWith("http") ? u : BACKEND + u;

  const cost = 12 * sizes.length;

  return (
    <div className="space-y-4" data-testid="static-ads-view">
      {versions.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#dcd3bc] bg-white/60 p-8 text-center text-slate-500">
          <FileImage className="w-10 h-10 mx-auto text-slate-300" />
          <div className="mt-3 text-sm">
            Lock at least one copy variant in the AI Copywriter tab, then generate ads from it.
          </div>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-[#ebe4d3] bg-white p-5 shadow-sm space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Source copy (locked)</div>
              <div className="space-y-1">
                {versions.map((v) => (
                  <label key={v.id}
                    className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer transition ${
                      selectedVer === v.id
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-[#ebe4d3] bg-white hover:bg-[#f5efdd]"
                    }`}>
                    <input type="radio" name="ver" value={v.id}
                      checked={selectedVer === v.id}
                      onChange={() => setSelectedVer(v.id)}
                      data-testid={`ads-ver-${v.id}`}
                      className="mt-1" />
                    <div className="flex-1">
                      <div className="text-sm font-bold text-slate-900 line-clamp-1">{v.headline}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {v.framework} · {v.tone} · {v.length}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Sizes</div>
              <div className="flex flex-wrap gap-1.5">
                {AD_SIZE_DEFS.map((sd) => {
                  const on = sizes.includes(sd.key);
                  return (
                    <button key={sd.key} onClick={() => toggleSize(sd.key)}
                      data-testid={`ads-size-${sd.key}`}
                      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                        on ? "border-indigo-500 bg-indigo-500 text-white" : "border-[#dcd3bc] bg-white hover:border-indigo-300 text-slate-700"
                      }`}>
                      {on && <CheckCircle2 className="w-3 h-3" />}
                      {sd.key} · {sd.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 pt-2">
              <div className="text-xs text-slate-500">
                Cost: <span className="font-bold text-slate-900">{cost} credits</span> · powered by Gemini Nano Banana
              </div>
              <button onClick={generate} disabled={busy || !selectedVer || sizes.length === 0}
                data-testid="ads-generate"
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white px-4 py-2 text-sm font-bold disabled:opacity-40">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                Generate ads
              </button>
            </div>
            {error && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm px-3 py-2 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
              </div>
            )}
          </div>

          {assets.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#dcd3bc] bg-white/60 p-8 text-center text-slate-500 text-sm">
              No ads yet. Pick a locked copy variant and sizes, then Generate.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {assets.map((a) => {
                const sd = AD_SIZE_DEFS.find((x) => x.key === a.format) || AD_SIZE_DEFS[0];
                return (
                  <div key={a.id} className={`rounded-xl border overflow-hidden bg-white ${
                      a.approved ? "border-emerald-300" : "border-[#ebe4d3]"
                    }`} data-testid={`ads-asset-${a.id}`}>
                    <div className="bg-[#faf5e8] grid place-items-center overflow-hidden"
                          style={{ aspectRatio: sd.aspect }}>
                      <img src={resolveUrl(a.url)} alt=""
                           className="w-full h-full object-cover" />
                    </div>
                    <div className="p-3 flex items-center justify-between gap-2">
                      <div>
                        <div className="text-xs font-bold text-slate-900">{a.format}</div>
                        <div className="text-[10px] text-slate-500">
                          {a.status === "ok" ? "Generated" : "Placeholder — regen"}
                          {a.approved && " · Approved"}
                        </div>
                      </div>
                      <a href={resolveUrl(a.url)} download target="_blank" rel="noopener noreferrer"
                         data-testid={`ads-download-${a.id}`}
                         className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Open
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}


/* ==========================================================================
   Landing Page view
   ========================================================================== */
function LandingView({ product }) {
  const [pages, setPages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [versionCount, setVersionCount] = useState(0);
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

  const load = async () => {
    const [ls, vs] = await Promise.all([
      studioLandingList(),
      studioCopyVersions(product.id),
    ]);
    setPages((ls.items || []).filter((p) => p.product_id === product.id));
    setVersionCount(((vs.items || []).filter((v) => v.locked)).length);
  };
  useEffect(() => { load(); }, [product.id]); // eslint-disable-line

  const generate = async () => {
    setBusy(true); setError("");
    try {
      await studioLandingGenerate({ product_id: product.id });
      await load();
    } catch (e) {
      setError(creditsErrorText(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="landing-view">
      <div className="rounded-2xl border border-[#ebe4d3] bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Layout className="w-4 h-4 text-indigo-600" /> Campaign landing page
            </div>
            <div className="text-xs text-slate-600 mt-1">
              Turn the best locked copy variant into a mobile-first landing page you can share, print or attach to an ad.
            </div>
          </div>
          <button onClick={generate}
            disabled={busy || versionCount === 0}
            data-testid="landing-generate"
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white px-4 py-2 text-sm font-bold disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {pages.length ? "Generate another" : "Generate landing page"}
          </button>
        </div>
        {versionCount === 0 && (
          <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Lock a copy variant first — the landing page inherits its headline, body and CTA.
          </div>
        )}
        {error && (
          <div className="mt-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm px-3 py-2 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
          </div>
        )}
      </div>

      {pages.length > 0 && (
        <div className="space-y-3">
          {pages.map((p) => (
            <div key={p.id} className="rounded-2xl border border-[#ebe4d3] bg-white overflow-hidden"
                 data-testid={`landing-page-${p.id}`}>
              <div className="flex items-start justify-between gap-3 p-4 border-b border-[#f0e9d6]">
                <div>
                  <div className="text-sm font-bold text-slate-900 line-clamp-1">{p.headline}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    /studio/landing/{p.slug} · {p.approved ? "Approved" : "Awaiting approval"}
                  </div>
                </div>
                <a href={BACKEND + p.preview_url}
                   target="_blank" rel="noopener noreferrer"
                   data-testid={`landing-open-${p.id}`}
                   className="inline-flex items-center gap-1 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white px-3 py-2 text-xs font-bold">
                  Preview <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <iframe src={BACKEND + p.preview_url}
                       className="w-full h-96 border-0"
                       title={p.headline}
                       loading="lazy" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ==========================================================================
   Approvals inbox
   ========================================================================== */
function ApprovalsTab() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ counts: {} });
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        studioApprovalsList({ status }),
        studioApprovalsSummary(),
      ]);
      setItems(r.items || []);
      setSummary(s);
    } catch (e) { setError(creditsErrorText(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const decide = async (id, decision) => {
    try {
      await studioApprovalsDecide(id, decision, null);
      await load();
    } catch (e) { alert(creditsErrorText(e)); }
  };

  return (
    <div className="space-y-4" data-testid="approvals-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-2xl font-bold text-slate-900">Approvals inbox</div>
          <div className="text-sm text-slate-600">
            Every draft — copy, ads, landing pages — lands here before it can ship.
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {[
            { key: "pending", label: "Pending", count: summary.counts?.pending || 0 },
            { key: "approved", label: "Approved", count: summary.counts?.approved || 0 },
            { key: "rejected", label: "Rejected", count: summary.counts?.rejected || 0 },
            { key: "changes_requested", label: "Changes", count: summary.counts?.changes_requested || 0 },
          ].map((s) => (
            <button key={s.key} onClick={() => setStatus(s.key)}
              data-testid={`approvals-status-${s.key}`}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                status === s.key
                  ? "border-indigo-500 bg-indigo-500 text-white"
                  : "border-[#dcd3bc] bg-white hover:border-indigo-300 text-slate-700"
              }`}>
              {s.label} <span className={`ml-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                status === s.key ? "bg-white/25" : "bg-slate-100"
              }`}>{s.count}</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-600 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#dcd3bc] bg-white/60 p-10 text-center text-slate-500">
          <Inbox className="w-10 h-10 mx-auto text-slate-300" />
          <div className="mt-3 text-sm">
            No {status.replace("_", " ")} items. {status === "pending" && "You're all caught up!"}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} className="rounded-xl border border-[#ebe4d3] bg-white p-4 shadow-sm flex items-start gap-3 flex-wrap"
                 data-testid={`approval-${it.id}`}>
              <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${
                  it.target?.kind === "creative_asset" ? "bg-fuchsia-100 text-fuchsia-700" :
                  it.target?.kind === "landing_page"   ? "bg-emerald-100 text-emerald-700" :
                  it.target?.kind === "creative_version" ? "bg-indigo-100 text-indigo-700" :
                  "bg-slate-100 text-slate-700"
                }`}>
                {it.target?.kind === "creative_asset" ? <FileImage className="w-4 h-4" /> :
                 it.target?.kind === "landing_page" ? <Layout className="w-4 h-4" /> :
                 <MessageSquare className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                  {it.target?.kind?.replace("_", " ") || "item"} · created {new Date(it.created_at).toLocaleString()}
                </div>
                <div className="text-sm font-semibold text-slate-900 mt-0.5 line-clamp-1">
                  {it.target?.label || it.target?.id}
                </div>
                {it.reason && (
                  <div className="text-xs text-slate-600 italic mt-0.5">Reason: {it.reason}</div>
                )}
              </div>
              {it.decision === "pending" ? (
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => decide(it.id, "approved")}
                    data-testid={`approval-approve-${it.id}`}
                    className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white px-3 py-1.5 text-xs font-bold">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                  </button>
                  <button onClick={() => decide(it.id, "changes_requested")}
                    data-testid={`approval-changes-${it.id}`}
                    className="inline-flex items-center gap-1 rounded-lg border border-[#dcd3bc] hover:border-amber-400 hover:bg-amber-50 text-amber-800 px-3 py-1.5 text-xs font-bold">
                    <Clock className="w-3.5 h-3.5" /> Changes
                  </button>
                  <button onClick={() => decide(it.id, "rejected")}
                    data-testid={`approval-reject-${it.id}`}
                    className="inline-flex items-center gap-1 rounded-lg border border-[#dcd3bc] hover:border-rose-400 hover:bg-rose-50 text-rose-800 px-3 py-1.5 text-xs font-bold">
                    <XCircle className="w-3.5 h-3.5" /> Reject
                  </button>
                </div>
              ) : (
                <div className={`text-[11px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${
                    it.decision === "approved" ? "bg-emerald-100 text-emerald-800" :
                    it.decision === "rejected" ? "bg-rose-100 text-rose-800" :
                    "bg-amber-100 text-amber-800"
                  }`}>
                  {it.decision.replace("_", " ")}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {error && (
        <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}
    </div>
  );
}


/* ==========================================================================
   Bulk CSV Import panel — appears in the Campaigns sidebar
   ========================================================================== */
function BulkImportPanel({ onDone }) {
  const inputRef = useRef(null);
  const [job, setJob] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const start = async (file) => {
    if (!file) return;
    setBusy(true); setError("");
    try {
      const res = await studioBulkImportCsv(file);
      setJob({ id: res.job_id, total: res.total, done: 0, ok: 0, fails: 0, status: "running", errors: [] });
      poll(res.job_id);
    } catch (e) {
      setError(creditsErrorText(e));
    } finally { setBusy(false);
      if (inputRef.current) inputRef.current.value = ""; }
  };

  const poll = (id) => {
    let tries = 0;
    const t = setInterval(async () => {
      tries++;
      try {
        const j = await studioBulkJob(id);
        setJob(j);
        if (j.status === "completed" || tries > 240) {
          clearInterval(t);
          onDone && onDone();
        }
      } catch { clearInterval(t); }
    }, 2500);
  };

  return (
    <div className="rounded-2xl border border-[#ebe4d3] bg-white p-4 shadow-sm">
      <div className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-1.5">
        <FileUp className="w-3.5 h-3.5 text-indigo-600" /> Bulk import CSV
      </div>
      <div className="text-xs text-slate-600 mb-2">
        Upload a CSV whose first column contains URLs — we'll import your whole catalogue overnight.
      </div>
      <label className={`block cursor-pointer rounded-lg border-2 border-dashed py-4 text-center transition ${
          busy ? "border-indigo-300 bg-indigo-50" : "border-[#dcd3bc] hover:border-indigo-400 hover:bg-indigo-50"
        }`}>
        {busy ? <Loader2 className="w-5 h-5 text-indigo-500 animate-spin mx-auto" /> :
          <div className="text-xs font-semibold text-slate-600">
            <Upload className="w-4 h-4 inline mr-1" /> Choose CSV file
          </div>}
        <input ref={inputRef} type="file" className="hidden"
          data-testid="campaigns-import-csv"
          accept=".csv,text/csv,text/plain"
          onChange={(e) => start(e.target.files?.[0])} />
      </label>
      <div className="mt-1 text-[10px] text-slate-500">Up to 200 URLs · 3 credits per row</div>
      {error && (
        <div className="mt-2 text-xs text-rose-700 flex items-start gap-1">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {error}
        </div>
      )}
      {job && (
        <div className="mt-3 rounded-lg border border-[#f0e9d6] bg-[#faf5e8] p-3 space-y-1 text-xs" data-testid="campaigns-bulk-job">
          <div className="flex items-center justify-between font-semibold">
            <span>
              {job.status === "completed" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 inline mr-1" /> :
                <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1 text-indigo-600" />}
              {job.status === "completed" ? "Complete" : "Importing…"}
            </span>
            <span className="font-mono">{job.done}/{job.total}</span>
          </div>
          <div className="h-1.5 bg-white rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-indigo-500 to-emerald-500 transition-all"
                 style={{ width: `${job.total ? (job.done / job.total) * 100 : 0}%` }} />
          </div>
          <div className="flex justify-between text-[10px] text-slate-600">
            <span>✓ {job.ok} imported</span>
            <span>{job.fails > 0 && `✕ ${job.fails} failed`}</span>
          </div>
        </div>
      )}
    </div>
  );
}

